import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { agentApi } from '../services/agentApi';
import { AppNav } from '../components/AppNav';
import { NoAgencyBanner } from '../components/NoAgencyBanner';
import { CreateClientModal } from '../components/CreateClientModal';
import { Button } from '../components/ui/button';
import { Users, Phone, Mail, ChevronRight, Plus } from 'lucide-react';

interface Client {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  assigned_agent_id: string | null;
}

function ClientCardSkeleton() {
  return (
    <div className="border border-border rounded-xl p-5 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <div className="h-4 bg-muted rounded w-2/3" />
          <div className="h-3 bg-muted rounded w-1/2" />
        </div>
        <div className="flex gap-3">
          <div className="h-3 bg-muted rounded w-10" />
          <div className="h-3 bg-muted rounded w-10" />
        </div>
      </div>
    </div>
  );
}

function AgentDashboardPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [noAgency, setNoAgency] = useState(false);

  useEffect(() => {
    document.title = 'Clientes — Takarah';
    agentApi.listClients()
      .then(setClients)
      .catch((err) => {
        if (err?.response?.status === 403) {
          setNoAgency(true);
        } else {
          setError('No se pudieron cargar los clientes');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (noAgency) return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <NoAgencyBanner />
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-5 h-5 text-primary" />
              <h1 className="text-2xl font-semibold tracking-tight">Mis clientes</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              {loading ? '—' : `${clients.length} cliente${clients.length !== 1 ? 's' : ''} en tu agencia`}
            </p>
          </div>
          <Button onClick={() => setShowModal(true)} size="sm" className="shrink-0 gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            Nuevo cliente
          </Button>
        </div>

        {error && <p className="text-sm text-destructive mb-4">{error}</p>}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <ClientCardSkeleton key={i} />)}
          </div>
        ) : clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-medium">Sin clientes aún</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs mb-5">
              Crea el primer perfil de cliente para empezar a compartir propiedades.
            </p>
            <Button onClick={() => setShowModal(true)} size="sm" className="gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              Nuevo cliente
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((client) => (
              <button
                key={client.id}
                onClick={() => navigate(`/agent/clients/${client.id}`)}
                className="w-full text-left border border-border rounded-xl p-5 hover:border-primary/30 hover:bg-muted/30 transition-all active:scale-[0.99] group"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold truncate">{client.name}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-muted-foreground text-xs">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />{client.email}
                      </span>
                      {client.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />{client.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0 ml-3" />
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {showModal && (
        <CreateClientModal
          onClose={() => setShowModal(false)}
          onCreated={(client) => {
            setClients((prev) => [client, ...prev]);
            setShowModal(false);
          }}
        />
      )}
    </div>
  );
}

export default AgentDashboardPage;
